import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { PageProps, User } from "../types/auth";

import styles from './styles/MemberSkillsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Level {
  level_id: number;
  name: string;
  sequence: number;
}

interface Skill {
  skill_id: number;
  name: string;
  status: "complete" | "incomplete";
  completed_at?: string;
  actor_username?: string;
}

interface LevelDetails {
  enrolled_date?: string;
  completed_date?: string;
  skills: Skill[];
  skills_count: number;
  completed_skills_count: number;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

interface LevelSequence {
  level_id: number;
  name: string;
  sequence: number;
}

interface LocationState {
  selectedCurriculum: number;
}

interface PromotionAction {
  levelId: number;
  levelName: string;
}

const MemberSkillsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const location = useLocation();
  const { selectedCurriculum } = (location.state as LocationState);
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [curriculumName, setCurriculumName] = useState<string>("");
  const [levels, setLevels] = useState<Level[]>([]);
  const [activeLevelID, setActiveLevelID] = useState<number | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [levelDetails, setLevelDetails] = useState<Record<number, LevelDetails>>({});
  const [mode, setMode] = useState<"owner/instructor" | "member">("owner/instructor");
  const [error, setError] = useState<string>("");
  const [promotionToConfirm, setPromotionToConfirm] = useState<PromotionAction | null>(null);
  const [demotionToConfirm, setDemotionToConfirm] = useState<PromotionAction | null>(null);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const memberResponse = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!memberResponse.ok) {
          const errorData = await memberResponse.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const memberData: Member = await memberResponse.json();
        setMemberInfo(memberData || null);

        // Fetch curriculum name
        const curriculumResponse = await fetch(`${API_BASE_URL}/api/curriculums/${selectedCurriculum}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (curriculumResponse.ok) {
          const curriculumData = await curriculumResponse.json();
          setCurriculumName(curriculumData.name || "");
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id && selectedCurriculum) {
      fetchMemberDetails();
    } else if (!selectedCurriculum) {
      setError("No curriculum selected. Please go back and select a curriculum.");
    }
  }, [member_id, token, selectedOrgID, selectedCurriculum]);

  useEffect(() => {
    const fetchLevels = async () => {
      if (!selectedCurriculum) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}/curriculums/${selectedCurriculum}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculum levels.");
          return;
        }

        const data = await response.json();
        const sortedLevels = Array.isArray(data.levels_sequence)
          ? (data.levels_sequence as LevelSequence[]).sort((a, b) => a.sequence - b.sequence)
          : [];
        setLevels(sortedLevels);

        if (data.active_level_id) {
          setActiveLevelID(data.active_level_id);
          setExpandedRow(data.active_level_id);
          fetchLevelDetails(data.active_level_id);
        }
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchLevels();
  }, [selectedCurriculum, token, selectedOrgID, member_id]);

  const toggleRow = (levelID: number) => {
    if (expandedRow === levelID) {
      setExpandedRow(null);
    } else {
      setExpandedRow(levelID);
      if (!levelDetails[levelID]) {
        fetchLevelDetails(levelID);
      }
    }
  };

  const fetchLevelDetails = async (levelID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${member_id}/levels/${levelID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });
      const details: LevelDetails = await response.json();
      if (!details || typeof details !== "object") {
        setLevelDetails((prev) => ({ 
          ...prev, 
          [levelID]: { 
            skills: [], 
            skills_count: 0,
            completed_skills_count: 0
          } 
        }));
        return;
      }
      setLevelDetails((prev) => ({ ...prev, [levelID]: details }));
    } catch {
      console.error("Failed to fetch level details.");
    }
  };

  const handleSkillToggle = async (levelID: number, skillID: number, completed: boolean) => {
    const userID = parseInt(member_id || "0", 10);
    try {
      const url = completed
        ? `${API_BASE_URL}/api/levels/${levelID}/skills/${skillID}/users`
        : `${API_BASE_URL}/api/levels/${levelID}/skills/${skillID}/users/${userID}`;
  
      const method = completed ? "POST" : "DELETE";
      const body = completed ? JSON.stringify({ user_id: userID }) : undefined;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body,
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update skill status.");
      }

      setLevelDetails((prev: Record<number, LevelDetails>): Record<number, LevelDetails> => {
        const updatedLevel = prev[levelID];
        if (!updatedLevel) return prev;

        const newState: Record<number, LevelDetails> = {
          ...prev,
          [levelID]: {
            ...updatedLevel,
            completed_skills_count: updatedLevel.completed_skills_count + (completed ? 1 : -1),
            skills: updatedLevel.skills.map(skill => 
              skill.skill_id === skillID 
                ? { ...skill, status: completed ? "complete" : "incomplete" }
                : skill
            )
          }
        };
        return newState;
      });
    } catch (err) {
      if (err instanceof Error) {
        console.error("Error updating skill status:", err.message);
      }
    }
  };

  const handlePromote = async (levelID: number) => {
    try {
      await fetch(`${API_BASE_URL}/api/curriculums/promoteUser`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          curriculum_id: selectedCurriculum,
          user_id: parseInt(member_id || "0", 10),
        }),
      });

      setActiveLevelID(levelID);
      setPromotionToConfirm(null);
    } catch (err) {
      setError("Failed to promote the user.");
    }
  };

  const handleDemote = async (levelID: number) => {
    try {
      await fetch(`${API_BASE_URL}/api/curriculums/demoteUser`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          curriculum_id: selectedCurriculum,
          user_id: parseInt(member_id || "0", 10),
        }),
      });

      setActiveLevelID(levelID);
      setDemotionToConfirm(null);
    } catch (err) {
      setError("Failed to demote the user.");
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  if (!memberInfo) {
    return <p className={styles.loadingMessage}>Loading member details...</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Levels/Skills</h1>
      <div className={styles.memberInfo}>
        <strong>Member:</strong>{" "}
        <a href={`/members/${member_id}`} className={styles.memberLink}>
          {memberInfo.display_name ? `${memberInfo.display_name} (${memberInfo.username})` : memberInfo.username}
        </a>
      </div>

      {curriculumName && (
        <div className={styles.curriculumInfo}>
          <strong>Curriculum:</strong> {curriculumName}
        </div>
      )}

      {levels.length > 0 ? (
        <div>
          <h3>Levels</h3>
          <table className={styles.table}>
            <tbody>
              {levels.map((level) => (
                <React.Fragment key={level.level_id}>
                  <tr className={styles.tableRow}>
                    <td className={styles.tableCell} onClick={() => toggleRow(level.level_id)}>
                      <strong>
                        {level.level_id === activeLevelID
                          ? `${level.name} (Currently Active)`
                          : level.name}
                      </strong>
                    </td>
                    <td className={styles.actionCell}>
                      {activeLevelID === 0 && level.sequence === 1 && (
                        <button
                          className={styles.promoteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPromotionToConfirm({ levelId: level.level_id, levelName: level.name });
                          }}
                        >
                          Promote
                        </button>
                      )}
                      {activeLevelID !== null && (() => {
                        const activeLevel = levels.find((lvl) => lvl.level_id === activeLevelID);
                        return activeLevel && level.sequence === activeLevel.sequence - 1 && (
                          <button
                            className={styles.demoteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDemotionToConfirm({ levelId: level.level_id, levelName: level.name });
                            }}
                          >
                            Demote
                          </button>
                        );
                      })()}
                      {activeLevelID !== null && (() => {
                        const activeLevel = levels.find((lvl) => lvl.level_id === activeLevelID);
                        return activeLevel && level.sequence === activeLevel.sequence + 1 && (
                          <button
                            className={styles.promoteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPromotionToConfirm({ levelId: level.level_id, levelName: level.name });
                            }}
                          >
                            Promote
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                  {expandedRow === level.level_id && levelDetails[level.level_id] && (
                    <tr>
                      <td className={styles.tableCell} colSpan={3}>
                        <div className={styles.detailsPane}>
                          <p>
                            <strong>Completion Status:</strong>{" "}
                            {!levelDetails[level.level_id]?.enrolled_date
                              ? "Not Started"
                              : levelDetails[level.level_id]?.completed_date
                              ? "Completed"
                              : "In Progress"}
                          </p>
                          {levelDetails[level.level_id]?.enrolled_date && (
                            <p>
                              <strong>Enrolled Date:</strong>{" "}
                              {new Date(levelDetails[level.level_id]?.enrolled_date || "").toLocaleDateString()}
                            </p>
                          )}
                          {levelDetails[level.level_id]?.completed_date && (
                            <p>
                              <strong>Completed Date:</strong>{" "}
                              {new Date(levelDetails[level.level_id]?.completed_date || "").toLocaleDateString()}
                            </p>
                          )}
                          <h4 className={styles.detailsTitle}>Skills (completed {levelDetails[level.level_id]?.completed_skills_count} of {levelDetails[level.level_id]?.skills_count})</h4>
                          <ul className={styles.skillsGrid}>
                            {(levelDetails[level.level_id]?.skills || []).map((skill) => (
                              <li key={skill.skill_id} className={styles.skillItem}>
                                <label className={styles.skillLabel}>
                                  <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={skill.status === "complete"}
                                    disabled={mode === "member"}
                                    onChange={(e) => handleSkillToggle(expandedRow, skill.skill_id, e.target.checked)}
                                  />
                                  {skill.name}
                                  {skill.status === "complete" && skill.completed_at && skill.actor_username && (
                                    <span className={styles.skillTooltip}>
                                      Granted {formatDate(skill.completed_at)} by {skill.actor_username}
                                    </span>
                                  )}
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No levels found for this curriculum.</p>
      )}

      {promotionToConfirm && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setPromotionToConfirm(null)} />
          <div className={styles.dialog}>
            <p>
              Promote this member to "{promotionToConfirm.levelName}"?
            </p>
            <div className={styles.dialogButtons}>
              <button className={styles.confirmButton} onClick={() => handlePromote(promotionToConfirm.levelId)}>
                Yes, Promote
              </button>
              <button className={styles.cancelButton} onClick={() => setPromotionToConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {demotionToConfirm && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setDemotionToConfirm(null)} />
          <div className={styles.dialog}>
            <p>
              Demote this member to "{demotionToConfirm.levelName}"?
            </p>
            <div className={styles.dialogButtons}>
              <button className={styles.confirmDemoteButton} onClick={() => handleDemote(demotionToConfirm.levelId)}>
                Yes, Demote
              </button>
              <button className={styles.cancelButton} onClick={() => setDemotionToConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MemberSkillsPage; 